import * as React from "react";
import { ReactComponent as RequiredIcon } from "../../assets/required-form.svg";
import { ReactComponent as ImageEmpty } from "../../assets/image-empty.svg";
import { ReactComponent as IconUpload } from "../../assets/icon-upload.svg";
import { ReactComponent as IconEdit } from "../../assets/icon-edit.svg";
import { useFilePicker } from "use-file-picker";
import { SelectedFiles, FileErrors } from "use-file-picker/types";
import { FileTypeValidator, FileSizeValidator } from "use-file-picker/validators";
import Show from "../Show";
import Swal from "sweetalert2";

interface Props {
  label: string;
  description: string;
  cn?: string;
  isRequired?: boolean;
  errorMessage?: string | any;
  value: File | string | undefined;
  name?: string;
  onChange?: (file: File | undefined, name?: string) => void;
}

export const toBase64 = async (files: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(new Blob([files], { type: files?.type }));
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
};

const FileUploadFormRegistration: React.FC<Props> = ({
  label = "",
  description = "",
  cn,
  errorMessage = "",
  isRequired = false,
  value,
  name = "",
  onChange,
}) => {
  const [previewImage, setPreviewImage] = React.useState<string>();

  const handleSelectFile = (data: SelectedFiles<string>) => {
    const file = data?.plainFiles?.[0];
    const dataPreview = data?.filesContent?.[0]?.content;

    // Additional check for image file type
    if (file && !file.type.startsWith("image/")) {
      Swal.fire({
        title: "Format file tidak sesuai!",
        text: "Mohon unggah file gambar (JPG, JPEG, atau PNG)",
        icon: "error",
      });
      return;
    }

    onChange?.(file, name);
    setPreviewImage(dataPreview);
  };

  const handleErrorFile = (errors: FileErrors) => {
    // Check what type of error occurred
    if (errors.hasOwnProperty("fileSizeError")) {
      Swal.fire({
        title: "Ukuran file terlalu besar!",
        text: "Mohon sediakan file dengan ukuran maksimum 2mb",
        icon: "error",
      });
    } else if (errors.hasOwnProperty("fileTypeError")) {
      Swal.fire({
        title: "Format file tidak didukung!",
        text: "Mohon unggah file berformat JPG, JPEG, atau PNG",
        icon: "error",
      });
    } else {
      Swal.fire({
        title: "Terjadi kesalahan!",
        text: "Gagal mengunggah file. Silakan coba lagi.",
        icon: "error",
      });
    }
  };

  const fileValue = React.useCallback(async () => {
    if (typeof value === "string") return setPreviewImage(value);

    // Preview-nya state lokal: kalau value dikosongin dari luar (reset form /
    // "Mulai dari awal") dan ini ga ikut dibersihin, foto lama tetap kepajang
    // seolah-olah masih keupload.
    if (!value) return setPreviewImage(undefined);

    const imgValue = await toBase64(value as File);

    setPreviewImage(imgValue as string);
  }, [value]);

  const { openFilePicker } = useFilePicker({
    readAs: "DataURL",
    accept: ["image/jpg", "image/png", "image/jpeg"],
    onFilesSuccessfullySelected: handleSelectFile,
    onFilesRejected: handleErrorFile,
    multiple: false,
    validators: [
      new FileTypeValidator(["jpg", "png", "jpeg"]),
      new FileSizeValidator({ maxFileSize: 2 * 1024 * 1024 }),
    ],
  });

  const handleOpenFilePicker = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event?.preventDefault();

    openFilePicker();
  };

  // ikut value: pas mode edit, data prefill-nya baru datang setelah komponen
  // mount, jadi kalau cuma jalan sekali preview-nya ga pernah keisi.
  React.useEffect(() => {
    fileValue();
  }, [fileValue]);

  return (
    <div className={!!!cn ? "flex flex-col py-[8px] px-[24px] gap-y-[8px]" : cn}>
      <div className="flex gap-x-[2px]">
        <h1 className="text-[13px] text-[#6A7481] leading-none font-semibold m-0">{label}</h1>
        <Show when={isRequired}>
          <RequiredIcon className="w-[10 px] h-[10px]" />
        </Show>
      </div>

      <Show when={!!description}>
        <div className="flex gap-x-[2px]">
          <h1 className="text-[12px] text-[#1F2D3D] leading-[16px] font-[400] space-y-[1px] m-0">
            {description}
          </h1>
        </div>
      </Show>

      <div className="w-[140px] h-[120px] object-contain mt-[4px]">
        <Show when={!!previewImage} fallbackComponent={<ImageEmpty />}>
          <img
            src={previewImage}
            className="object-contain h-[120px] w-[140px]"
            alt="Image Preview"
            onError={(e) => {
              // Gagal muat preview bukan kesalahan user — bisa gara-gara URL
              // file lama yang udah ga ada di server. Cukup balik ke empty
              // state; jangan nembak modal error, karena ini kejadian pas form
              // baru dibuka dan user belum megang apa-apa.
              e.currentTarget.onerror = null;
              setPreviewImage(undefined);
            }}
          />
        </Show>
      </div>

      <button
        onClick={handleOpenFilePicker}
        className="text-[#1F2D3D] w-[140px] border-[1px] border-[#1F2D3D] rounded-[32px] mt-[4px] bg-white flex justify-center items-center py-[4px] px-[16px] gap-x-[6px]"
      >
        <Show
          when={!!previewImage}
          fallbackComponent={
            <React.Fragment>
              <IconUpload />
              <span className="leading-none font-semibold">Upload</span>
            </React.Fragment>
          }
        >
          <IconEdit />
          <span className="leading-none font-semibold">Edit</span>
        </Show>
      </button>

      {errorMessage && <span className="text-[11px] text-red-600">{errorMessage}</span>}
    </div>
  );
};

export default FileUploadFormRegistration;
